import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import type { Profile } from './types';

/**
 * Blocking somebody, from their profile.
 *
 * ## Blocking asks; unblocking does not
 *
 * Blocking severs the follow in both directions and there is no undo that puts
 * it back, so it gets a confirmation step. Unblocking takes nothing away and is
 * one press.
 *
 * ## Why it hides the other buttons
 *
 * A blocked profile showing Follow and Message beside Unblock offers two
 * actions the server will refuse. Better to show the one thing that works, and
 * make the state obvious, than to leave controls lying around that fail when
 * pressed.
 */
export function BlockButton({ profile }: { profile: Profile }) {
  const me = useSession((state) => state.user);
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);

  const mutation = useMutation({
    mutationFn: (block: boolean) =>
      block
        ? api.post(`/users/${profile.username}/block`)
        : api.delete(`/users/${profile.username}/block`),
    onSuccess: () => {
      setAsking(false);

      void client.invalidateQueries({ queryKey: ['profile', profile.username] });
      void client.invalidateQueries({ queryKey: ['blocks'] });
      /*
       * The dock may be holding a conversation with them that can no longer be
       * written to, and the feed and their comments are now filtered.
       */
      void client.invalidateQueries({ queryKey: ['conversations'] });
      void client.invalidateQueries({ queryKey: ['feed'] });
    },
  });

  if (!me || me.id === profile.id) {
    return null;
  }

  if (profile.is_blocked) {
    return (
      <Button
        variant="secondary"
        loading={mutation.isPending}
        onClick={() => mutation.mutate(false)}
        data-unblock-profile
      >
        Unblock
      </Button>
    );
  }

  if (asking) {
    return (
      <span className="flex items-center gap-2">
        <Button
          variant="danger"
          loading={mutation.isPending}
          onClick={() => mutation.mutate(true)}
          data-block-confirm
        >
          Block {profile.username}
        </Button>
        <Button variant="ghost" onClick={() => setAsking(false)}>
          Cancel
        </Button>
      </span>
    );
  }

  return (
    <Button variant="ghost" onClick={() => setAsking(true)} data-block-profile>
      Block
    </Button>
  );
}
